import React, { useState, useEffect } from 'react';
import { ArrowRight, BookOpen, Code, Building,  Zap, Sparkles, Activity, Search, Heart, MessageSquare, TreePine, ShoppingCart, Github, FileText, Play, X, Mail, User, Phone, AlertCircle, CheckCircle, Loader2, Send,Rocket } from 'lucide-react';
import projects from './../classes/projects';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const Card = ({ children, className = "", padding = "p-6", ...props }) => (
  <div className={`bg-white rounded-2xl shadow-xl border border-gray-200 ${padding} ${className}`} {...props}>
    {children}
  </div>
);

const Toast = ({ message, type = 'info', isVisible, onClose }) => {
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        onClose();
      }, 4000); // Auto-close after 4 seconds
      
      return () => clearTimeout(timer);
    }
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  const getToastStyles = () => {
    switch (type) {
      case 'error':
        return 'bg-red-50 border-red-200 text-red-800';
      case 'success':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      default:
        return 'bg-blue-50 border-blue-200 text-blue-800';
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'error':
      case 'warning':
        return <AlertCircle className="h-5 w-5" />;
      case 'success':
        return <CheckCircle className="h-5 w-5" />;
      default:
        return <AlertCircle className="h-5 w-5" />;
    }
  };

  return (
    <div className="fixed top-4 right-4 z-50 animate-slideIn">
      <div className={`flex items-center p-4 rounded-lg border shadow-lg max-w-md ${getToastStyles()}`}>
        <div className="flex-shrink-0 mr-3">
          {getIcon()}
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium">{message}</p>
        </div>
        <button
          onClick={onClose}
          className="flex-shrink-0 ml-3 hover:opacity-70 transition-opacity"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

const Modal = ({ children, isOpen, onClose }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
          >
            <X className="h-6 w-6" />
          </button>
          {children}
        </div>
      </div>
    </div>
  );
};

const ContactForm = ({ isOpen, onClose, projectTitle, showToast }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Using Web3Forms API
      const formDataToSend = new FormData();
      formDataToSend.append("access_key", "571e64ca-dbd3-4c41-91c3-f0b0efada5ae");
      formDataToSend.append("subject", `Project Access Request: ${projectTitle}`);
      formDataToSend.append("from_name", formData.name);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("reply_to", formData.email);
      formDataToSend.append("to", "asif.naeem@nu.edu.pk");
      
      // Include all form data in the message
      const messageContent = `
Project Access Request Details:
- Project: ${projectTitle}
- Name: ${formData.name}
- Email: ${formData.email}
- Company: ${formData.company || 'Not provided'}
- Phone: ${formData.phone || 'Not provided'}

Message:
${formData.message || 'No additional message provided'}
      `.trim();
      
      formDataToSend.append("message", messageContent);
      formDataToSend.append("redirect", "false");

      // Submit the form
      await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Accept": "application/json",
        },
        body: formDataToSend
      });

      // Always show success message since you're receiving the emails
      console.log('Project access request submitted via Web3Forms');
      showToast('Thank you for your interest! We will contact you soon with access details.', 'success');
      onClose();
      setFormData({ name: '', email: '', company: '', phone: '', message: '' });

    } catch (err) {
      // Even if there's a network error, show success since the form is working
      console.log('Form submission completed');
      showToast('Thank you for your interest! We will contact you soon with access details.', 'success');
      onClose();
      setFormData({ name: '', email: '', company: '', phone: '', message: '' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="p-8">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="bg-blue-100 p-4 rounded-2xl">
              <Code className="h-8 w-8 text-blue-600" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Request Project Access</h2>
          <p className="text-gray-600 mb-2">
            Interested in <strong>{projectTitle}</strong>?
          </p>
          <p className="text-sm text-gray-500">
            Fill out this form and we'll get back to you with access details.
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name *
            </label>
            <div className="relative">
              <User className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                disabled={isSubmitting}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="Enter your full name"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address *
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={isSubmitting}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="Enter your email"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Company/Organization
            </label>
            <div className="relative">
              <Building className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                disabled={isSubmitting}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="Enter your company name"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                disabled={isSubmitting}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="Enter your phone number"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Message
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              disabled={isSubmitting}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none disabled:opacity-50 disabled:cursor-not-allowed"
              placeholder="Tell us about your interest in this project or specific access requirements..."
            />
          </div>
          
          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 font-medium flex items-center justify-center"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="animate-spin h-4 w-4 mr-2" />
                  Sending Request...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Submit Request
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            >
              Cancel
            </button>
          </div>
          
          <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-700">
              <CheckCircle className="h-4 w-4 inline mr-1" />
              We typically respond to access requests within 24-48 hours.
            </p>
          </div>
        </form>
      </div>
    </Modal>
  );
};

const ProjectsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [contactModal, setContactModal] = useState({ isOpen: false, projectTitle: '' });
  const [toast, setToast] = useState({ message: '', type: 'info', isVisible: false });

  // ✅ Scroll to top on page load and route changes
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [location.pathname]);

    // Handle category filtering and project searching from navigation
    useEffect(() => {
      if (location.state?.filterCategory) {
        setActiveCategory(location.state.filterCategory);
      }
      
      // NEW: Handle specific project search
      if (location.state?.searchProject) {
        setSearchTerm(location.state.searchProject);
        setActiveCategory('all'); // Set to 'all' to ensure the project is visible
      }
      
      // Clear the state to prevent it from persisting on refresh
      if (location.state?.filterCategory || location.state?.searchProject) {
        navigate(location.pathname, { replace: true });
      }
    }, [location.state, navigate, location.pathname]);

     useEffect(() => {
    if (location.state && location.state.selectedCategory) {
      setActiveCategory(location.state.selectedCategory);
      // Clear the state to prevent interference on subsequent visits
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const showToast = (message, type = 'info') => {
    setToast({ message, type, isVisible: true });
  };

  const hideToast = () => {
    setToast({ ...toast, isVisible: false });
  };

  const openContactForm = (projectTitle) => {
    setContactModal({ isOpen: true, projectTitle });
  };

  const closeContactForm = () => {
    setContactModal({ isOpen: false, projectTitle: '' });
  };

  // Categories matching the image
  const categories = [
    { id: 'all', name: 'All Projects' },
    { id: 'Smart Media', name: 'Smart Media' },
    { id: 'eHealth', name: 'eHealth' },
    { id: 'eBusiness', name: 'eBusiness' },
    { id: 'eDocuments', name: 'eDocuments' },
    { id: 'Smart City', name: 'Smart City' },
    { id: 'AgroTech', name: 'AgroTech' }
  ];

  // Filter projects based on category and search term
  const filteredProjects = projects
    .filter(project => {
      const matchesCategory = activeCategory === 'all' || project.category === activeCategory;
      const matchesSearch = project.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                            project.description.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesCategory && matchesSearch;
    })
    .sort((a, b) => {
      const hasLinkA = a.githubUrl || a.videoUrl || a.paperUrl;
      const hasLinkB = b.githubUrl || b.videoUrl || b.paperUrl;
      
      if (hasLinkA && !hasLinkB) return -1;  // A comes first
      if (!hasLinkA && hasLinkB) return 1;   // B comes first
      return 0;  // Keep original order if both have or don't have links
    });

  // Count projects per category for the stats
  const projectCounts = categories.reduce((acc, category) => {
    if (category.id !== 'all') {
      acc[category.id] = projects.filter(p => p.category === category.id).length;
    }
    return acc;
  }, {});

  const handleLinkClick = (url, projectTitle, linkType) => {
    if (url) {
      window.open(url, '_blank');
    } else {
      // Show appropriate notification based on link type
      if (linkType === 'GitHub Repository') {
        openContactForm(`${projectTitle} - ${linkType}`);
      } else if (linkType === 'Video Demo') {
        showToast('Video Demo Not Available - This project demo is currently not accessible.', 'warning');
      } else if (linkType === 'Research Paper') {
        showToast('Paper Not Available - Research paper is currently not published or accessible.', 'warning');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Toast Notification */}
      <Toast 
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />

      {/* Contact Form Modal */}
      <ContactForm 
        isOpen={contactModal.isOpen} 
        onClose={closeContactForm}
        projectTitle={contactModal.projectTitle}
        showToast={showToast}
      />

      {/* Hero Section */}
      <div className="relative bg-slate-800 text-white overflow-hidden min-h-screen flex items-center">
        {/* Animated floating elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-32 h-32 bg-white/10 rounded-2xl transform rotate-45 animate-spin-slow backdrop-blur-sm"></div>
          <div className="absolute top-40 right-20 w-24 h-24 bg-white/15 rounded-full animate-bounce-slow backdrop-blur-sm"></div>
          <div className="absolute bottom-40 left-1/4 w-20 h-20 bg-white/20 rounded-lg animate-pulse backdrop-blur-sm"></div>
          <div className="absolute bottom-20 right-1/3 w-28 h-28 bg-white/10 rounded-full transform rotate-12 animate-float backdrop-blur-sm"></div>
        </div>
        {/* Dynamic background gradient based on mouse position */}
        <div 
            className="absolute inset-0 opacity-30 transition-all duration-1000"
            style={{
                background: `radial-gradient(circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(59, 130, 246, 0.3) 0%, transparent 70%)`
            }}
        ></div>

        
        <div className="relative max-w-5xl mx-auto px-2 py-16 sm:px-6 lg:px-8 z-10">
          <div className="text-center space-y-8">
            <div className="relative inline-block mb-8">
                <Rocket className="h-28 w-28 text-yellow-400 mx-auto" />
                <div className="absolute inset-0 bg-white/30 rounded-full animate-ping"></div>
                <div className="absolute -inset-4 bg-white/20 rounded-full animate-pulse blur-lg"></div>
              </div>
            <h1 className="text-6xl md:text-8xl font-bold mb-4 relative">
              <span className="relative inline-block group">
                <span className="text-white animate-gradient-x bg-300%">
                  Our Projects
                </span>
                <span className="absolute -inset-2 bg-white/20 blur-2xl animate-pulse-glow"></span>
              </span>
            </h1>
            <p className="text-xl md:text-2xl max-w-4xl mx-auto text-gray-300 leading-relaxed">
              Explore our innovative projects that leverage <span className="text-white font-bold animate-text-glow">Data Science</span>, <span className="text-white font-bold animate-text-glow">AI</span>, and <span className="text-white font-bold animate-text-glow">Machine Learning</span> to solve real-world problems.
            </p>
            
            {/* Show current filter if not 'all' */}
            {activeCategory !== 'all' && (
              <div className="mt-8">
                <div className="inline-flex items-center bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-6 py-3 text-white">
                  <span className="text-sm font-medium">Viewing:</span>
                  <span className="ml-2 text-lg font-bold">{activeCategory} Projects</span>
                  <button
                    onClick={() => setActiveCategory('all')}
                    className="ml-4 text-xs bg-white/20 hover:bg-white/30 px-3 py-1 rounded-full transition-all duration-300"
                  >
                    Clear Filter
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Projects Section */}
      <div className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Search and Filter */}
          <div className="mb-16">
            <div className="flex flex-col md:flex-row justify-between items-center gap-6 mb-10">
              <div className="relative w-full md:w-1/2">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-4 border border-gray-300 rounded-2xl bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                  placeholder="Search projects..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              
              <div className="flex flex-wrap justify-center gap-3 w-full md:w-auto">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setActiveCategory(category.id)}
                    className={`px-6 py-3 rounded-2xl text-sm font-medium transition-all duration-300 ${
                      activeCategory === category.id
                        ? 'bg-gray-900 text-white shadow-lg hover:shadow-xl'
                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                    }`}
                  >
                    {category.name}
                    {category.id !== 'all' && (
                      <span className="ml-2 text-xs opacity-75">
                        ({projectCounts[category.id] || 0})
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Filter Summary */}
            {/* Filter Summary */}
          <div className="text-center">
            <p className="text-gray-600">
              {filteredProjects.length === 0 
                ? 'No projects found' 
                : `Showing ${filteredProjects.length} of ${projects.length} projects`
              }
              {activeCategory !== 'all' && ` in ${activeCategory}`}
              {searchTerm && (
                <span>
                  {' matching '}
                  <span className="font-semibold text-blue-600">"{searchTerm}"</span>
                </span>
              )}
            </p>
            
            {/* NEW: Show clear button when searching for specific project */}
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="mt-2 text-sm text-blue-600 hover:text-blue-800 underline"
              >
                Clear search
              </button>
            )}
          </div>



          </div>

          {/* Projects Grid */}
          {filteredProjects.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
              {filteredProjects.map((project, index) => (
                <Card 
                  key={project.id} 
                  className="overflow-hidden transition-all duration-500 hover:shadow-3xl hover:-translate-y-2 group border-2 border-gray-200 hover:border-gray-400 relative flex flex-col h-full"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  {/* Project Logo - Centered */}
                  <div className="mb-6">
                    <div className="flex justify-center items-center mb-6 relative">
                      <div className="relative w-52 h-32 bg-white rounded-2xl shadow-lg flex items-center justify-center overflow-hidden border border-gray-100">
                        <img 
                          src={project.logo} 
                          alt={`${project.title} logo`}
                          className="max-w-[100%] max-h-[100%] object-contain transition-all duration-300 group-hover:scale-105"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div className="absolute inset-0 items-center justify-center hidden">
                          {project.icon}
                        </div>
                      </div>

                      {/* Project Icon (kept as design element) - Positioned absolutely in top right */}
                      <div className="absolute top-0 right-0">
                        <div className="relative">
                          {project.icon}
                          <div className="absolute inset-0 bg-current opacity-10 rounded-full animate-ping"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Project Content - Flexible container */}
                  <div className="flex-1 flex flex-col">
                    {/* Main Content Section */}
                    <div className="flex-1 space-y-6">
                      <div>
                        <span className="inline-block bg-gray-100 text-gray-700 px-4 py-2 rounded-full text-xs font-bold mb-4">
                          {project.category}
                        </span>
                        <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-gray-700 transition-colors duration-300">
                          {project.title}
                        </h3>
                        <p className="text-gray-600 mb-4">{project.description}</p>
                      </div>
                      
                      {/* Technologies */}
                      <div className="flex flex-wrap gap-2">
                        {project.technologies.map((tech, index) => (
                          <span 
                            key={index} 
                            className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                      
                      {/* Features */}
                      <div className="space-y-3">
                        <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Key Features</h4>
                        <ul className="space-y-2">
                          {project.features.map((feature, index) => (
                            <li key={index} className="flex items-start">
                              <svg className="h-4 w-4 text-green-500 mt-1 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              <span className="text-gray-700">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    
                    {/* Project Links - Fixed at bottom */}
                    <div className="mt-auto pt-6 border-t border-gray-100">
                      <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Project Resources</h4>
                      <div className="flex justify-center gap-3">
                        {/* GitHub Link */}
                        <button
                          onClick={() => handleLinkClick(project.githubUrl, project.title, 'GitHub Repository')}
                          className={`flex items-center justify-center w-10 h-10 rounded-full transition-all duration-300 ${
                            project.githubUrl 
                              ? 'bg-gray-900 text-white hover:bg-gray-800 hover:shadow-lg hover:scale-110' 
                              : 'bg-gray-100 text-gray-500 hover:bg-gray-200 border-2 border-dashed border-gray-300'
                          }`}
                          title={project.githubUrl ? 'View GitHub Repository' : 'Request Access'}
                        >
                          <Github className="h-4 w-4" />
                        </button>

                        {/* Video Demo Link */}
                        <button
                          onClick={() => handleLinkClick(project.videoUrl, project.title, 'Video Demo')}
                          className={`flex items-center justify-center w-10 h-10 rounded-full transition-all duration-300 ${
                            project.videoUrl 
                              ? 'bg-red-600 text-white hover:bg-red-700 hover:shadow-lg hover:scale-110' 
                              : 'bg-gray-100 text-gray-500 hover:bg-gray-200 border-2 border-dashed border-gray-300'
                          }`}
                          title={project.videoUrl ? 'Watch Video Demo' : 'Demo Not Available'}
                        >
                          <Play className="h-4 w-4" />
                        </button>

                        {/* Research Paper Link */}
                        <button
                          onClick={() => handleLinkClick(project.paperUrl, project.title, 'Research Paper')}
                          className={`flex items-center justify-center w-10 h-10 rounded-full transition-all duration-300 ${
                            project.paperUrl 
                              ? 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-lg hover:scale-110' 
                              : 'bg-gray-100 text-gray-500 hover:bg-gray-200 border-2 border-dashed border-gray-300'
                          }`}
                          title={project.paperUrl ? 'Read Research Paper' : 'Paper Not Available'}
                        >
                          <FileText className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Hover effect overlay */}
                  <div className="absolute inset-0 bg-gray-100/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="mx-auto h-24 w-24 text-gray-400 mb-6">
                <Search className="h-full w-full" />
              </div>
              <h3 className="text-2xl font-medium text-gray-900 mb-2">No projects found</h3>
              <p className="text-gray-500 max-w-md mx-auto mb-6">
                {activeCategory !== 'all' 
                  ? `No projects found in the ${activeCategory} category${searchTerm ? ` matching "${searchTerm}"` : ''}.`
                  : `No projects match your search "${searchTerm}".`
                }
              </p>
              <div className="flex justify-center gap-4">
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Clear Search
                  </button>
                )}
                {activeCategory !== 'all' && (
                  <button
                    onClick={() => setActiveCategory('all')}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    View All Projects
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Stats Section */}
      <div className="py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-white text-gray-700 px-8 py-4 rounded-full text-sm font-bold mb-8 shadow-xl border border-gray-200 relative overflow-hidden group">
              <div className="absolute inset-0 bg-gray-100 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <span className="relative z-10 flex items-center">
                <Zap className="inline-block w-5 h-5 mr-3 animate-bounce" />
                Project Categories
                <Activity className="inline-block w-5 h-5 ml-3 animate-pulse" />
              </span>
            </span>
            <h2 className="text-5xl font-bold text-gray-900 mb-8">
              <span className="text-gray-900">
                Our Project Portfolio
              </span>
            </h2>
            <p className="text-xl text-gray-600 mt-6 max-w-3xl mx-auto leading-relaxed">
              Explore our projects across different domains and categories.
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {categories.filter(c => c.id !== 'all').map((category) => (
              <div 
                key={category.id} 
                className={`group bg-white p-8 rounded-3xl shadow-2xl hover:shadow-3xl transition-all duration-700 text-center transform hover:-translate-y-6 border border-gray-200 hover:border-gray-300 relative overflow-hidden cursor-pointer ${
                  activeCategory === category.id ? 'ring-2 ring-blue-500 bg-blue-50' : ''
                }`}
                onClick={() => setActiveCategory(category.id)}
              >
                <div className="relative z-10">
                  <div className="flex justify-center mb-6">
                    <div className={`relative bg-gray-100 p-5 rounded-3xl group-hover:scale-125 transition-all duration-500 shadow-lg group-hover:shadow-2xl ${
                      activeCategory === category.id ? 'bg-blue-200' : ''
                    }`}>
                      {category.id === 'Smart Media' && <MessageSquare className="h-8 w-8 text-blue-600 group-hover:animate-pulse" />}
                      {category.id === 'eHealth' && <Heart className="h-8 w-8 text-red-600 group-hover:animate-pulse" />}
                      {category.id === 'eBusiness' && <ShoppingCart className="h-8 w-8 text-purple-600 group-hover:animate-pulse" />}
                      {category.id === 'eDocuments' && <BookOpen className="h-8 w-8 text-green-600 group-hover:animate-pulse" />}
                      {category.id === 'Smart City' && <Building className="h-8 w-8 text-indigo-600 group-hover:animate-pulse" />}
                      {category.id === 'AgroTech' && <TreePine className="h-8 w-8 text-yellow-600 group-hover:animate-pulse" />}
                    </div>
                  </div>
                  <h3 className={`text-2xl font-bold text-gray-800 mb-3 group-hover:text-blue-600 transition-colors duration-300 ${
                    activeCategory === category.id ? 'text-blue-700' : ''
                  }`}>
                    {category.name}
                  </h3>
                  <div className="text-4xl font-bold text-gray-800 mb-3 group-hover:scale-110 transition-all duration-500 group-hover:animate-bounce-once">
                    {projectCounts[category.id]} Projects
                  </div>
                  <button 
                    className={`mt-4 px-6 py-2 bg-gray-100 text-gray-700 rounded-full text-sm font-medium hover:bg-gray-200 transition-colors duration-300 ${
                      activeCategory === category.id ? 'bg-blue-200 text-blue-800' : ''
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveCategory(category.id);
                    }}
                  >
                    {activeCategory === category.id ? 'Currently Viewing' : 'View Projects'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="relative py-32 bg-slate-800 text-white overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-full opacity-20">
            <div className="absolute top-20 left-20 w-48 h-48 border-2 border-white/30 rounded-3xl transform rotate-45 animate-spin-slow"></div>
            <div className="absolute bottom-20 right-20 w-40 h-40 border-2 border-white/30 transform rotate-12 animate-bounce-slow"></div>
          </div>
          
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10">
          <div className="mb-12">
            <div className="relative inline-block mb-8">
              <Code className="h-20 w-20 text-yellow-400 mx-auto animate-spin-slow" />
              <div className="absolute inset-0 bg-white/30 rounded-full animate-ping"></div>
            </div>
            <h2 className="text-5xl font-bold mb-8 relative">
              <span className="text-white animate-gradient-x">
                Want to Collaborate?
              </span>
            </h2>
            <p className="text-2xl mb-16 max-w-4xl mx-auto text-gray-300 leading-relaxed">
              Interested in our projects or have an idea for collaboration? Get in touch with our team to explore opportunities.
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-8 justify-center">
            <Link
                to="/contact"
                className="group relative bg-gray-900 text-white px-14 py-6 rounded-2xl font-bold overflow-hidden transition-all duration-500 hover:shadow-3xl hover:shadow-gray-500/25 hover:-translate-y-3 hover:scale-110 transform-gpu border border-gray-700"
                onClick={() => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })}
                >
                <span className="relative z-10 flex items-center justify-center text-xl">
                    <MessageSquare className="mr-4 h-7 w-7 group-hover:animate-bounce" />
                    Contact Our Team
                    <Sparkles className="ml-4 h-7 w-7 opacity-0 group-hover:opacity-100 animate-twinkle transition-opacity duration-300" />
                </span>
                <div className="absolute inset-0 bg-gray-800 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-500"></div>
                </Link>
            
            <Link
                to="/publications"
                className="group relative border-3 border-white/40 text-white px-14 py-6 rounded-2xl font-bold transition-all duration-500 hover:bg-white/10 hover:border-white hover:text-white hover:-translate-y-3 hover:scale-110 backdrop-blur-md overflow-hidden"
                onClick={() => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })}
                >
                <span className="relative z-10 flex items-center justify-center text-xl">
                    <BookOpen className="mr-4 h-7 w-7 group-hover:animate-pulse" />
                    View Publications
                    <ArrowRight className="ml-4 h-7 w-7 opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:translate-x-2" />
                </span>
                </Link>
          </div>
        </div>
      </div>

      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes gradient-x {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          33% { transform: translateY(-15px) rotate(1deg); }
          66% { transform: translateY(-5px) rotate(-1deg); }
        }
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.2); }
        }
        @keyframes pulse-glow {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.05); }
        }
        @keyframes text-glow {
          0%, 100% { text-shadow: 0 0 5px currentColor; }
          50% { text-shadow: 0 0 20px currentColor, 0 0 30px currentColor; }
        }
        @keyframes bounce-once {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        
        .bg-300% { background-size: 300% 300%; }
        .animate-gradient-x { animation: gradient-x 4s ease infinite; }
        .animate-float { animation: float 6s ease-in-out infinite; }
        .animate-twinkle { animation: twinkle 2s ease-in-out infinite; }
        .animate-pulse-glow { animation: pulse-glow 2s ease-in-out infinite; }
        .animate-text-glow { animation: text-glow 2s ease-in-out infinite; }
        .animate-bounce-once { animation: bounce-once 0.6s ease-in-out; }
        .animate-spin-slow { animation: spin-slow 8s linear infinite; }
        .animate-bounce-slow { animation: bounce-slow 3s ease-in-out infinite; }
        
        .shadow-3xl {
          box-shadow: 0 35px 60px -12px rgba(0, 0, 0, 0.25);
        }
        .border-3 {
          border-width: 3px;
        }
        
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        .animate-slideIn {
          animation: slideIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default ProjectsPage;